import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useCallback } from "react";

// 加载dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);

const convertToTargetTimezone = (dateValue, targetTimezone, format) => {
    const date = targetTimezone === "UTC" ? dayjs.utc(dateValue) : dayjs.utc(dateValue).tz(targetTimezone);
    return date.format(format);
};

// 常见的日期字段列表，用于自动检测
export const COMMON_DATE_FIELDS = ["flight_date", "std", "sta", "etd", "eta", "atd", "atd_icao", "ata", "ata_icao", "schedule_date", "start_date", "end_date"];
const DATE_ONLY_FIELDS = new Set(["flight_date", "schedule_date", "start_date", "end_date"]);

/**
 * 格式化API查询参数中的日期
 * 将各种日期格式转换为后端期望的YYYY-MM-DD格式
 * @param {Date|string|Object} dateValue - 日期值
 * @param {string} format - 目标格式，默认为YYYY-MM-DD
 * @returns {string|null} 格式化后的日期字符串，无效时返回null
 */
export const formatDateParam = (dateValue, format = "YYYY-MM-DD") => {
    if (!dateValue) return null;

    try {
        const date = dayjs.utc(dateValue);
        if (date.isValid()) {
            return date.format(format);
        }
        return null;
    } catch (error) {
        console.error("日期格式化失败:", error);
        return null;
    }
};

/**
 * 检测API响应中的日期字段
 * @param {Object|Array} data - API响应数据
 * @param {Array} knownDateFields - 已知的日期字段列表
 * @param {Boolean} detectNested - 是否检测嵌套对象
 * @returns {Array} 检测到的日期字段列表
 */
export const detectDateFields = (data, knownDateFields = COMMON_DATE_FIELDS, detectNested = true) => {
    // 如果数据为空或非对象/数组，直接返回空数组
    if (!data || (typeof data !== "object" && !Array.isArray(data))) {
        return [];
    }

    const detectedFields = new Set();
    const datePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

    // 检查单个字段是否是日期
    const checkField = (fieldName, value) => {
        // 如果是已知日期字段，直接添加
        if (knownDateFields.includes(fieldName)) {
            detectedFields.add(fieldName);
            return;
        }

        // 检查字段值是否符合日期格式
        if (typeof value === "string" && datePattern.test(value)) {
            // 验证是否是有效日期
            const date = dayjs(value);
            if (date.isValid()) {
                detectedFields.add(fieldName);
            }
        }
    };

    // 递归检查对象或数组
    const checkObject = (obj) => {
        if (Array.isArray(obj)) {
            // 如果是数组，检查第一个元素
            if (obj.length > 0 && typeof obj[0] === "object") {
                checkObject(obj[0]);
            }
        } else if (obj && typeof obj === "object") {
            // 遍历对象的每个属性
            Object.entries(obj).forEach(([key, value]) => {
                checkField(key, value);

                // 如果需要检测嵌套对象且值是对象或数组，递归检查
                if (detectNested && value && typeof value === "object") {
                    checkObject(value);
                }
            });
        }
    };

    checkObject(data);
    return Array.from(detectedFields);
};

/**
 * 处理API响应中的日期时间字段
 * @param {Object|Array} data - API响应数据
 * @param {Object} options - 处理选项
 * @returns {Object|Array} 处理后的数据
 */
export const processApiResponse = (data, options = {}) => {
    if (!data) return data;

    const {
        dateFields = [],
        timezone = null, // 默认使用当前上下文时区
        detectNested = true,
        convertToLocal = true, // 是否转换为本地时间
    } = options;

    // 如果不需要转换为本地时间，则直接返回数据
    if (!convertToLocal) {
        return data;
    }

    // 如果没有指定日期字段，自动检测
    const fieldsToProcess = dateFields.length > 0 ? dateFields : detectDateFields(data, COMMON_DATE_FIELDS, detectNested);

    if (fieldsToProcess.length === 0) return data;

    // 处理单个对象
    const processObject = (obj) => {
        if (!obj || typeof obj !== "object") return obj;

        const result = { ...obj };

        fieldsToProcess.forEach((field) => {
            if (field in result && result[field]) {
                try {
                    // 确保值是有效日期
                    const dateValue = result[field];
                    const date = dayjs(dateValue);

                    if (date.isValid()) {
                        if (timezone) {
                            // 转换为指定时区
                            result[field] = convertToTargetTimezone(dateValue, timezone, dateValue.includes("T") ? "YYYY-MM-DDTHH:mm:ss" : "YYYY-MM-DD");
                        }
                    }
                } catch (error) {
                    console.error(`处理日期字段 ${field} 出错:`, error);
                }
            }
        });

        // 递归处理嵌套对象
        if (detectNested) {
            Object.entries(result).forEach(([key, value]) => {
                if (value && typeof value === "object") {
                    if (Array.isArray(value)) {
                        result[key] = value.map((item) => (typeof item === "object" ? processObject(item) : item));
                    } else {
                        result[key] = processObject(value);
                    }
                }
            });
        }

        return result;
    };

    // 处理数组或对象
    if (Array.isArray(data)) {
        return data.map((item) => (typeof item === "object" ? processObject(item) : item));
    }

    return processObject(data);
};

/**
 * API处理器Hook - 增强API调用函数
 * @param {Function} apiFunction - 原始API调用函数
 * @param {Object} options - 处理选项
 * @returns {Function} 增强的API调用函数
 */
export const useApiProcessor = (apiFunction, options = {}) => {
    return useCallback(
        async (...args) => {
            try {
                const response = await apiFunction(...args);
                return processApiResponse(response, {
                    ...options,
                    timezone: options.timezone || null,
                });
            } catch (error) {
                throw error;
            }
        },
        [apiFunction, options],
    );
};

/**
 * 增强API模块 - 将普通API模块转换为自动处理日期时间的版本
 * @param {Object} apiModule - 原始API模块
 * @param {Object} options - 处理选项
 * @returns {Object} 增强的API模块
 */
export const enhanceApiModule = (apiModule, options = {}) => {
    if (!apiModule || typeof apiModule !== "object") {
        throw new Error("API模块必须是一个对象");
    }

    const enhancedModule = {};

    // 包装所有API方法
    Object.entries(apiModule).forEach(([key, value]) => {
        if (typeof value === "function") {
            // 函数直接包装
            enhancedModule[key] = async (...args) => {
                // 调用原始API函数，获取包含data的response对象
                const response = await value(...args);

                // 检查response和response.data是否存在
                if (response && response.data) {
                    // 对 response.data 进行处理
                    const processedData = processApiResponse(response.data, options);
                    // 将处理后的数据放回 response.data
                    response.data = processedData;
                }
                // 总是返回完整的 response 对象
                return response;
            };
        } else if (typeof value === "object" && value !== null) {
            // 递归增强嵌套对象
            enhancedModule[key] = enhanceApiModule(value, options);
        } else {
            // 其他值直接复制
            enhancedModule[key] = value;
        }
    });

    return enhancedModule;
};

/**
 * 准备API提交数据 - 将本地时区日期转换为UTC
 * @param {Object} data - 要提交的数据
 * @param {Array} dateFields - 需要处理的日期字段
 * @returns {Object} 处理后的数据
 */
export const prepareApiSubmit = (data, dateFields = []) => {
    if (!data || typeof data !== "object") return data;

    const result = { ...data };

    // 处理指定的日期字段
    dateFields.forEach((field) => {
        if (field in result && result[field]) {
            try {
                const dateValue = result[field];
                // 假设 dateValue 已经是 UTC (Day.js UTC 对象或可解析的 UTC 字符串)
                const date = dayjs.utc(dateValue);

                if (date.isValid()) {
                    // 日期字段只提交日历日期；datetime 字段必须保留明确的 UTC 时区。
                    result[field] = DATE_ONLY_FIELDS.has(field) ? date.format("YYYY-MM-DD") : date.toISOString();
                }
            } catch (error) {
                console.error(`准备提交日期字段 ${field} 出错:`, error);
            }
        }
    });

    // 递归处理嵌套对象
    Object.entries(result).forEach(([key, value]) => {
        if (value && typeof value === "object") {
            if (Array.isArray(value)) {
                result[key] = value.map((item) => (typeof item === "object" ? prepareApiSubmit(item, dateFields) : item));
            } else {
                result[key] = prepareApiSubmit(value, dateFields);
            }
        }
    });

    return result;
};

/**
 * 处理Dayjs特殊对象 - 转换包含$y,$M,$D等字段的对象为标准格式
 * @param {Object} obj - 需要处理的对象
 * @param {Array} dateFields - 可能包含Dayjs对象的字段列表
 * @returns {Object} 处理后的对象
 */
export const processDayjsObjects = (obj, dateFields = ["start_date", "end_date", "flight_date", "std", "sta"]) => {
    if (!obj || typeof obj !== "object") return obj;

    const result = { ...obj };

    // 遍历指定字段
    dateFields.forEach((field) => {
        if (result[field] && typeof result[field] === "object") {
            // 检查是否是Dayjs特殊格式对象
            if (result[field].$y !== undefined && result[field].$M !== undefined && result[field].$D !== undefined) {
                try {
                    // 判断是否包含时间信息
                    const hasTime = result[field].$H !== undefined || result[field].$m !== undefined || result[field].$s !== undefined;

                    // 构建dayjs对象
                    let date = dayjs()
                        .year(result[field].$y)
                        .month(result[field].$M) // Dayjs月份是0-11
                        .date(result[field].$D);

                    // 如果有时间信息，添加时间部分
                    if (hasTime) {
                        date = date
                            .hour(result[field].$H || 0)
                            .minute(result[field].$m || 0)
                            .second(result[field].$s || 0);

                        // 使用带时间的格式
                        result[field] = date.format("YYYY-MM-DDTHH:mm:ss");
                    } else {
                        // 使用纯日期格式
                        result[field] = date.format("YYYY-MM-DD");
                    }
                } catch (error) {
                    console.error(`处理Dayjs对象字段 ${field} 失败:`, error);
                }
            }
        }
    });

    return result;
};
